import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { env } from '@/config/env'
import { authStorage } from '@/utils/storage'
import { isImpersonating, resolveAuthToken, stripImpersonationTokenFromUrl } from '@/utils/impersonation'
import { ApiResponse } from '@/types'
import { handleApiError } from '@/utils/error-handler'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: env.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      config => {
        const token = resolveAuthToken()
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        // Don't set Content-Type for FormData, let browser set it with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    // Response interceptor - Transform response and handle errors
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse<any>>) => {
        // Unwrap the data property from the response
        return {
          ...response,
          data: response.data.data,
        }
      },
      error => {
        // Handle 401 - Unauthorized (token expired or invalid)
        if (error.response?.status === 401) {
          // Check if user was authenticated (had a token) before clearing
          const hadToken = !!resolveAuthToken()
          const impersonating = isImpersonating()

          // Only redirect to login if:
          // 1. User was not authenticated (no token), OR
          // 2. We're on a public/auth page (login, forgot-password, reset-password)
          // Don't redirect if user was authenticated and on a protected page - let component handle the error
          const isPublicPage =
            window.location.pathname.startsWith('/login') ||
            window.location.pathname.startsWith('/reset-password')

          if (impersonating) {
            stripImpersonationTokenFromUrl()
          } else {
            authStorage.clear()
          }
          
          if (!hadToken || isPublicPage) {
            // User was not authenticated or on public page - redirect to login
            if (!isPublicPage) {
              window.location.href = '/login'
            }
          }
          // If user was authenticated and on protected page, don't redirect
          // Let the component handle the error (could be permission issue or expired token)
        }

        // Transform error to user-friendly message string
        const errorMessage = handleApiError(error)
        return Promise.reject(new Error(errorMessage))
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<ApiResponse<T>>(url, config)
    return response.data as T
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config)
    return response.data as T
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config)
    return response.data as T
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config)
    return response.data as T
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url, config)
    return response.data as T
  }
}

export const apiClient = new ApiClient()
